import { CoveredPeriodStrings } from "@impero-com/common/utils/formatting";
import { TFunction } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import memoizeOne from "memoize-one";
import moment from "moment-timezone";

import { CoveredPeriodInterval } from "api/generated/model";
import { getCurrentLanguage } from "utils/locale";

function formatFinancialYear(details: { start: string; end: string }) {
    const sy = moment(details.start).year();
    const ey = moment(details.end).year();
    return sy !== ey ? sy + "-" + ey : sy.toString();
}

export function formatPeriodStrings(
    t: TFunction,
    period: CoveredPeriodInterval | undefined,
    culture?: string
): CoveredPeriodStrings {
    if (!period) {
        return {
            label: "",
            sortString: "",
        };
    }
    if (!culture) {
        culture = getCurrentLanguage();
    }
    const startDate = moment(period.range.start).locale(culture);
    const yearText = period.financialYearDetails
        ? t("Financial_Year_Short") +
          " " +
          formatFinancialYear(period.financialYearDetails)
        : startDate.year().toString();
    let text: string;
    switch (period.period.type) {
        case "Month": {
            text = startDate.format("MMMM Y");
            break;
        }
        case "TwoMonths": {
            switch (period.period.data) {
                case "JanuaryFebruary":
                    text = t("TwoMonth_1") + " " + yearText;
                    break;
                case "MarchApril":
                    text = t("TwoMonth_2") + " " + yearText;
                    break;
                case "MayJune":
                    text = t("TwoMonth_3") + " " + yearText;
                    break;
                case "JulyAugust":
                    text = t("TwoMonth_4") + " " + yearText;
                    break;
                case "SeptemberOctober":
                    text = t("TwoMonth_5") + " " + yearText;
                    break;
                case "NovemberDecember":
                    text = t("TwoMonth_6") + " " + yearText;
                    break;
                default:
                    return assertNever(period.period);
            }
            break;
        }
        case "Quarter": {
            switch (period.period.data) {
                case "First":
                    text = t("Quarter_1") + " " + yearText;
                    break;
                case "Second":
                    text = t("Quarter_2") + " " + yearText;
                    break;
                case "Third":
                    text = t("Quarter_3") + " " + yearText;
                    break;
                case "Fourth":
                    text = t("Quarter_4") + " " + yearText;
                    break;
                default:
                    return assertNever(period.period);
            }
            break;
        }
        case "Half": {
            switch (period.period.data) {
                case "First":
                    text = t("Half_1") + " " + yearText;
                    break;
                case "Second":
                    text = t("Half_2") + " " + yearText;
                    break;
                default:
                    return assertNever(period.period);
            }
            break;
        }
        case "Year": {
            text = period.financialYearDetails
                ? yearText
                : t("Year") + " " + yearText;
            break;
        }
        default: {
            return assertNever(period.period);
        }
    }
    return {
        label: text,
        sortString: text,
    };
}

const PERIOD_LABEL_REGEX = /^(.+)( [0-9]+(-[0-9]+)?)$/i;

const periodTechToValue: (t: TFunction) => { [tech: string]: string } =
    memoizeOne((t: TFunction) => ({
        M1: t("Month_1"),
        M2: t("Month_2"),
        M3: t("Month_3"),
        M4: t("Month_4"),
        M5: t("Month_5"),
        M6: t("Month_6"),
        M7: t("Month_7"),
        M8: t("Month_8"),
        M9: t("Month_9"),
        M10: t("Month_10"),
        M11: t("Month_11"),
        M12: t("Month_12"),

        T1: t("TwoMonth_1"),
        T2: t("TwoMonth_2"),
        T3: t("TwoMonth_3"),
        T4: t("TwoMonth_4"),
        T5: t("TwoMonth_5"),
        T6: t("TwoMonth_6"),

        Q1: t("Quarter_1"),
        Q2: t("Quarter_2"),
        Q3: t("Quarter_3"),
        Q4: t("Quarter_4"),

        H1: t("Half_1"),
        H2: t("Half_2"),

        Y: t("Year"),
    }));

export function formatPeriodLabel(t: TFunction, unformattedLabel: string) {
    const match = PERIOD_LABEL_REGEX.exec(unformattedLabel);
    if (match) {
        const prefix = match[1];
        const end = match[2];
        if (prefix === "FY") {
            return t("Financial_Year_Short") + end;
        }
        const fyIndex = prefix.length - 3;
        const hasFy = fyIndex > 0 && prefix.substring(fyIndex) === " FY";
        const tech = hasFy ? prefix.substring(0, fyIndex) : prefix;
        const value = periodTechToValue(t)[tech];
        if (value) {
            if (hasFy) {
                return value + " " + t("Financial_Year_Short") + end;
            } else {
                return value + end;
            }
        }
    }
    return undefined;
}
